import { useCallback } from "react"
import { graphql } from "react-relay"
import { t } from "i18next"
import { useMutationWithErrorDialog } from "@/hooks/useMutationWithErrorDialog"
import {
  useUploadMutation$data,
  useUploadMutation as useUploadMutationType,
} from "@/modules/upload/services/__generated__/useUploadMutation.graphql"

const mutation = graphql`
  mutation useUploadMutation($input: UploadAppointmentFilesInput!) {
    uploadAppointmentFiles(input: $input) {
      uploadAppointmentFilesOutput {
        ... on SuccessUploadAppointmentFilesOutput {
          attachments {
            id
            url
            thumbnailUrl
            uploadDate
            attachmentType
            uploadStatus
            fileName
            uploadSource
          }
          isSuccess
        }
        ... on ErrorUploadAppointmentFilesOutput {
          isSuccess
          error {
            fileName
            message
          }
        }
      }
    }
  }
`

type MutationParams = {
  appointmentId: string
  files: File[]
  handleError?: (error: Error | string | undefined, fileName?: string | null | undefined) => void
  handleCompleted?: (
    attachments: NonNullable<
      useUploadMutation$data["uploadAppointmentFiles"]["uploadAppointmentFilesOutput"]
    >["attachments"],
  ) => void
}

const useUploadMutation = () => {
  const [commit, isFlight] = useMutationWithErrorDialog<useUploadMutationType>(mutation, {
    confirmProps: {
      title: t("patient.uploadFiles.unableToUpload"),
    },
  })

  const commitHandler = useCallback(
    async ({ appointmentId, files, handleCompleted, handleError }: MutationParams) => {
      commit({
        variables: {
          input: {
            appointmentId,
            files,
          },
        },
        onCompleted({ uploadAppointmentFiles }) {
          const errorMessage = uploadAppointmentFiles.uploadAppointmentFilesOutput?.error?.message
          const fileName = uploadAppointmentFiles.uploadAppointmentFilesOutput?.error?.fileName
          const isSuccess = uploadAppointmentFiles.uploadAppointmentFilesOutput?.isSuccess
          const attachments = uploadAppointmentFiles.uploadAppointmentFilesOutput?.attachments

          if (isSuccess) {
            handleCompleted?.(attachments)
          } else {
            handleError?.(errorMessage, fileName)
          }
        },
        onError(error) {
          handleError?.(error.message)
        },
        updater: (store, data) => {
          const appointmentRecordProxy = store.get(appointmentId)
          if (!appointmentRecordProxy) return

          const appointmentAttachments = appointmentRecordProxy.getLinkedRecords("appointmentAttachments") || []
          const attachments = data?.uploadAppointmentFiles.uploadAppointmentFilesOutput?.attachments || []

          attachments.forEach((attachment) => {
            const attachmentId = attachment.id
            const existingAttachment = appointmentAttachments.find((att) => att.getDataID() === attachmentId)
            if (existingAttachment) return null

            const newAttachment = store.get(attachmentId)
            newAttachment?.setValue("UPLOADED", "uploadStatus")
            if (newAttachment) {
              appointmentAttachments.unshift(newAttachment)
            }
          })

          appointmentRecordProxy.setLinkedRecords(appointmentAttachments, "appointmentAttachments")
        },
      })
    },
    [commit],
  )

  return [commitHandler, isFlight] as const
}

export { useUploadMutation }
